/**
 * src/config/examCategories.js
 *
 * Centralized taxonomy lists for each exam.
 * Used by QuestionEditor, importService, and AdminDashboard.
 *
 * ── Taxonomy dimensions ──────────────────────────────────────────────────────
 *
 *   category           (existing, ALL exams)
 *     The primary lifecycle phase being tested.
 *     PMP:     Initiating | Planning | Execution | Monitoring and Controlling | Closing
 *     CPMAI:   Business Understanding | Data Understanding | Data Preparation |
 *              Model Development | Model Evaluation | Model Operationalization
  *     PMI-ACP: Mindset | Leadership | Product | Delivery (4-domain ECO)
 *
 *   performanceDomain  (new, PMP only, optional)
 *     The primary PMP performance domain emphasis.
 *     Allowed: People | Process | Business
 *
 *   deliveryContext    (new, PMP only, optional)
 *     The dominant delivery model or thematic context.
 *     Allowed: Predictive | Agile | Hybrid | AI
 *
 * Backward compatibility rules:
 *   • The new two dimensions are OPTIONAL.  Existing question banks load
 *     unchanged with both fields absent / null.
 *   • They are only validated/enforced for PMP questions.  For CPMAI the
 *     helpers below treat any value as acceptable and pass-through, and the
 *     editor/dashboard hide the UI entirely for CPMAI in this increment.
 *   • To add a new exam or update categories, edit only this file — no
 *     component changes are required.
 */

export const EXAM_CATEGORIES = {
  pmp: [
    'Initiating',
    'Planning',
    'Execution',
    'Monitoring and Controlling',
    'Closing',
  ],
  cpmai: [
    'Business Understanding',
    'Data Understanding',
    'Data Preparation',
    'Model Development',
    'Model Evaluation',
    'Model Operationalization',
  ],
  // 2026 PMI-ACP ECO four-domain model is the active vocabulary for both
  // `category` and `metadata.ecoDomain`. The legacy seven-domain list lives
  // in PMI_ACP_LEGACY_DOMAINS below as a forward-mapping reference only --
  // the editor dropdown, dashboards, and analytics all use this four-domain
  // list.
  'pmi-acp': [
    'Mindset',
    'Leadership',
    'Product',
    'Delivery',
  ],
  // PMI-PMOCP Exam Content Outline (2025) — six domains. These are the
  // `category` vocabulary for PMI-PMOCP questions, surfaced as "Domains"
  // (see exams.js metadataDisplay). To revise, edit only this list.
  'pmi-pmocp': [
    'Organizational Development and Alignment',
    'PMO Strategic Elements',
    'PMO Design and Structuring',
    'People',
    'PMO Operations and Performance',
    'PMO Enhancement and Effectiveness',
  ],
};

/**
 * PMP performance domain emphasis.  Applies ONLY to exam='pmp'.
 *
 *   People   — leadership, collaboration, conflict, team dynamics
 *   Process  — planning, governance, quality, risk, execution flow
 *   Business — benefits, strategy, value realization, compliance
 */
export const PMP_PERFORMANCE_DOMAINS = ['People', 'Process', 'Business'];

/**
 * PMP delivery style / thematic context.  Applies ONLY to exam='pmp'.
 *
 *   Predictive — waterfall baseline control
 *   Agile      — sprint / backlog / iterative delivery
 *   Hybrid     — mixed lifecycle
 *   AI         — AI-enabled delivery, AI governance, AI use in PM
 */
export const PMP_DELIVERY_CONTEXTS = ['Predictive', 'Agile', 'Hybrid', 'AI'];

/**
 * Returns true if the given category value is a valid (known) category
 * for the specified exam.  An empty/null value is considered valid
 * (category is optional).
 *
 * @param {string} examId  - 'pmp' | 'cpmai' | 'pmi-acp'
 * @param {string} value   - the category string to check
 * @returns {boolean}
 */
export function isKnownCategory(examId, value) {
  if (!value) return true;
  const list = EXAM_CATEGORIES[examId];
  if (!list) return true; // unknown exam — don't block
  return list.includes(value);
}

/**
 * Returns true if `value` is a valid performanceDomain for `examId`.
 *
 * Rules:
 *   • An empty/null value is ALWAYS valid (the field is optional).
 *   • Only enforced for PMP.  Any value passes for non-PMP exams — this
 *     increment intentionally does not define a taxonomy for CPMAI and
 *     non-PMP callers may pass-through whatever they have.
 *
 * @param {string} examId
 * @param {string|null|undefined} value
 * @returns {boolean}
 */
export function isKnownPerformanceDomain(examId, value) {
  if (value === null || value === undefined || value === '') return true;
  if (examId !== 'pmp') return true;
  return PMP_PERFORMANCE_DOMAINS.includes(value);
}

/**
 * Returns true if `value` is a valid deliveryContext for `examId`.
 *
 * Rules:
 *   • An empty/null value is ALWAYS valid (the field is optional).
 *   • Only enforced for PMP.  Any value passes for non-PMP exams.
 *
 * @param {string} examId
 * @param {string|null|undefined} value
 * @returns {boolean}
 */
export function isKnownDeliveryContext(examId, value) {
  if (value === null || value === undefined || value === '') return true;
  if (examId !== 'pmp') return true;
  return PMP_DELIVERY_CONTEXTS.includes(value);
}

/**
 * PMI-ACP ECO domains -- canonical 4-domain model (2026 revision).
 * Primary reporting dimension for PMI-ACP analytics, readiness, milestones,
 * journey, and study plan. The published ECO weights are:
 *
 *   Mindset     28%
 *   Leadership  25%
 *   Product     19%
 *   Delivery    28%
 */
export const PMI_ACP_ECO_DOMAINS = ['Mindset', 'Leadership', 'Product', 'Delivery'];

/**
 * PMI-ACP practice areas -- agile / scaled-agile frameworks and disciplines
 * the ECO draws from. Used by question metadata, targeted-test filters,
 * and analytics breakdowns.
 */
export const PMI_ACP_PRACTICE_AREAS = [
  'Scrum',
  'Kanban',
  'Lean',
  'XP',
  'SAFe',
  'DevOps',
  'Agile Coaching',
  'Product Ownership',
  'Value Stream Management',
  'Systems Thinking',
  'Enterprise Agility',
];

/**
 * Bloom-style cognitive levels. Applies to ALL exams, not just PMI-ACP.
 */
export const COGNITIVE_LEVELS = ['Recall', 'Understand', 'Apply', 'Analyze', 'Evaluate'];

/**
 * Question difficulty buckets. Applies to ALL exams.
 */
export const DIFFICULTIES = ['Easy', 'Medium', 'Hard', 'Expert'];

/**
 * Legacy PMI-ACP seven-domain vocabulary. Retained so that:
 *   1. The importer can detect legacy values in pre-2026 imports.
 *   2. Analytics can roll up legacy `category` values into the new ECO
 *      domains via PMI_ACP_LEGACY_TO_ECO_DOMAIN below.
 *   3. Existing questions tagged with these values are preserved as-is in
 *      the `category` column (we do NOT auto-rewrite stored data).
 */
export const PMI_ACP_LEGACY_DOMAINS = [
  'Agile Principles & Mindset',
  'Value-Driven Delivery',
  'Stakeholder Engagement',
  'Team Performance',
  'Adaptive Planning',
  'Problem Detection & Resolution',
  'Continuous Improvement',
];

/**
 * Roll-up map from legacy 7-domain category to the new ECO 4-domain.
 * Conservative best-effort assignment used by analytics when an older row
 * has a legacy `category` but no `eco_domain`.
 */
export const PMI_ACP_LEGACY_TO_ECO_DOMAIN = {
  'Agile Principles & Mindset':     'Mindset',
  'Value-Driven Delivery':          'Product',
  'Stakeholder Engagement':         'Leadership',
  'Team Performance':               'Leadership',
  'Adaptive Planning':              'Delivery',
  'Problem Detection & Resolution': 'Delivery',
  'Continuous Improvement':         'Mindset',
};

/**
 * Exam weight buckets (used in question metadata.examWeight). Indicates how
 * heavily a topic is represented on the live exam. Applies to ALL exams.
 */
export const EXAM_WEIGHTS = ['Low', 'Medium', 'High'];


/**
 * PMI-PMOCP domains — canonical 6-domain model (PMI Exam Content Outline 2025).
 * Primary reporting dimension for PMI-PMOCP analytics, readiness, study plan,
 * and Question Factory. Published ECO weights:
 *
 *   Organizational Development and Alignment   16%
 *   PMO Strategic Elements                      18%
 *   PMO Design and Structuring                  18%
 *   People                                      15%
 *   PMO Operations and Performance              15%
 *   PMO Enhancement and Effectiveness           18%
 */
export const PMI_PMOCP_DOMAINS = [
  'Organizational Development and Alignment',
  'PMO Strategic Elements',
  'PMO Design and Structuring',
  'People',
  'PMO Operations and Performance',
  'PMO Enhancement and Effectiveness',
];

export const PMI_PMOCP_DOMAIN_WEIGHTS = {
  'Organizational Development and Alignment': 16,
  'PMO Strategic Elements':                   18,
  'PMO Design and Structuring':               18,
  'People':                                   15,
  'PMO Operations and Performance':           15,
  'PMO Enhancement and Effectiveness':        18,
};


/**
 * PMI-PMOCP secondary dimension — Focus Areas. A finer-grained, cross-domain
 * practice/diagnostic layer that complements the six ECO domains (the primary
 * `category`). Declared per-cert via exams.js `secondaryDimension`; this list is
 * the allowed vocabulary. 'Unclassified' is the fallback for questions that
 * cannot be confidently classified (flagged for admin review).
 */
export const PMI_PMOCP_FOCUS_AREAS = [
  'PMO Strategy & Design',
  'Governance & Oversight',
  'Portfolio Alignment',
  'Benefits & Value Realization',
  'PMO Services',
  'Resource & Capacity Management',
  'Reporting & Analytics',
  'Organizational Change',
  'PMO Maturity',
  'Stakeholder & Executive Engagement',
];

export const FOCUS_AREA_UNCLASSIFIED = 'Unclassified';

/**
 * Per-cert secondary-dimension vocabularies. Config-driven and keyed by examId,
 * so adding a secondary dimension for another cert is a one-line addition here
 * (no per-cert conditionals in app code). exams.js `secondaryDimension`
 * references these same lists.
 */
export const SECONDARY_DIMENSION_VALUES = {
  'pmi-pmocp': PMI_PMOCP_FOCUS_AREAS,
};

/**
 * PMI-CPMAI ECO DOMAINS — the five scored domains of the September 2025
 * PMI-CPMAI Exam Content Outline, with their PMI weights:
 *
 *   Support Responsible and Trustworthy AI Efforts   15%
 *   Identify Business Needs and Solutions            26%
 *   Identify Data Needs                              26%
 *   Manage AI Model Development and Evaluation       16%
 *   Operationalize AI Solution                       17%
 *
 * ⚠️ These are NOT the six CPMAI METHODOLOGY PHASES (questions.category:
 * Business Understanding / Data Understanding / Data Preparation / Model
 * Development / Model Evaluation / Model Operationalization). The phases are
 * how the PMI prep course teaches; the domains are how PMI scores the exam.
 * They do not map one-to-one — Responsible & Trustworthy AI in particular is
 * cross-cutting and appears inside every phase. Never derive one from the other.
 *
 * Stored on questions.eco_domain. PMI-ACP also uses that column, with its OWN
 * vocabulary (Mindset / Leadership / Product / Delivery) — which is exactly why
 * the allowlist below is keyed by examId and must never be applied globally.
 */
export const CPMAI_ECO_DOMAINS = [
  'Support Responsible and Trustworthy AI Efforts',
  'Identify Business Needs and Solutions',
  'Identify Data Needs',
  'Manage AI Model Development and Evaluation',
  'Operationalize AI Solution',
];

/**
 * Certs whose ECO domain is REQUIRED on an active (publishable) question, with
 * their allowed vocabulary. Keyed by examId so no other certification is
 * affected: PMI-ACP is deliberately absent, so its own eco_domain values keep
 * flowing through untouched.
 *
 * Mirrored server-side by qf_canonical_cpmai_eco_domain() and the
 * questions_cpmai_eco_domain_ck CHECK constraint (migration 096). The database
 * is the backstop; this is the friendly, early half of the same rule.
 */
export const REQUIRED_ECO_DOMAIN_VALUES = {
  cpmai: CPMAI_ECO_DOMAINS,
};

/** True when `examId` must carry an ECO domain before a question can go active. */
export function isEcoDomainRequired(examId) {
  return Array.isArray(REQUIRED_ECO_DOMAIN_VALUES[examId]);
}

/**
 * Validates an ECO domain for a certification that requires one.
 *
 * STRICTER THAN isKnownFocusArea BY DESIGN. focus_area is an optional tag, so
 * null and 'Unclassified' pass there. An ECO domain is a publish precondition,
 * so null, empty, whitespace-only, non-string and out-of-allowlist all FAIL.
 * Certs without a required ECO domain always pass.
 *
 * @returns {{ ok: boolean, error: string|null }}
 */
export function validateEcoDomain(examId, value) {
  const allowed = REQUIRED_ECO_DOMAIN_VALUES[examId];
  if (!allowed) return { ok: true, error: null };

  if (value === null || value === undefined || value === '') {
    return { ok: false, error: `ecoDomain is required for ${examId}. Allowed values: ${allowed.join(', ')}` };
  }
  if (typeof value !== 'string') {
    return { ok: false, error: 'Invalid ecoDomain — must be a string.' };
  }
  const trimmed = value.trim();
  if (trimmed === '') {
    return { ok: false, error: `ecoDomain is required for ${examId}. Allowed values: ${allowed.join(', ')}` };
  }
  if (!allowed.includes(trimmed)) {
    return { ok: false, error: `Invalid ecoDomain "${value}" for ${examId}. Allowed values: ${allowed.join(', ')}` };
  }
  return { ok: true, error: null };
}

/** Convenience boolean wrapper around validateEcoDomain(). */
export function isValidEcoDomain(examId, value) {
  return validateEcoDomain(examId, value).ok;
}

/**
 * Returns true if `value` is a valid secondary-dimension value (focus area) for
 * `examId`. Empty/null is valid (optional); 'Unclassified' is always accepted;
 * a cert with no secondary dimension never blocks.
 */
export function isKnownFocusArea(examId, value) {
  if (value === null || value === undefined || value === '') return true;
  if (value === FOCUS_AREA_UNCLASSIFIED) return true;
  const list = SECONDARY_DIMENSION_VALUES[examId];
  if (!list) return true;
  return list.includes(value);
}


/**
 * Case Study Question Framework — curated THEMATIC tag vocabulary (Rev 3).
 *
 * Tags are admin-only discovery/authoring aids on case_studies.tags. They are
 * deliberately soft-governed: an unknown tag is a validation WARNING in the
 * case editor, never a publish blocker. Keep tags thematic — ECO
 * domain/task metadata stays the authoritative taxonomy and must not be
 * duplicated here. No learner-facing tag UI until the case library is large
 * enough to make discovery useful.
 */
export const CASE_STUDY_TAGS = [
  'stakeholder-conflict',
  'hybrid-governance',
  'organizational-change',
  'tailoring',
  'weak-matrix',
  'value-delivery',
  'servant-leadership',
  'team-empowerment',
  'vendor-management',
  'benefits-realization',
  'ethical-decision-making',
  'agile-suitability',
  'pilot-adoption',
  'resource-contention',
];

/**
 * Returns true when `tag` is in the curated case-study vocabulary. Unknown
 * tags are allowed (warning-only policy); this helper exists so the admin
 * editor can surface the warning consistently.
 */
export function isKnownCaseStudyTag(tag) {
  return CASE_STUDY_TAGS.includes(tag);
}
